import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import AdminPortalLayout from '@/modules/admin/components/AdminPortalLayout';
import AdminAccess from '@/shared/components/AdminAccess';
import AdminCreateAppModal from '@/modules/admin/components/AdminCreateAppModal';
import { useToast } from '@/hooks/useToast';
import { useAdminStats } from '@/hooks/queries';

export default function AdminPortalHomepage() {
  const { user, authFetch } = useAuthContext();
  const { showToast, toasts, hideToast } = useToast();
  const [showCreateAppModal, setShowCreateAppModal] = useState(false);
  
  const { data: stats = {
    quotesNeeded: 0,
    inQueue: 0,
    readyToLive: 0,
    live: 0,
    totalApps: 0,
    paymentIssues: 0,
    appsNoPaymentMethod: 0,
  }, isLoading: loadingStats } = useAdminStats();


  return (
    <AdminAccess>
      <AdminPortalLayout toasts={toasts} onCloseToast={hideToast}>
          {/* Stats Cards */}

          {/* Stats Cards */}
          {!loadingStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <Link
                to="/admin-portal/quotes-needed"
                className="bg-white rounded-xl border-2 border-blue-200 p-8 hover:border-blue-400 transition-all shadow-lg hover:shadow-xl"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-base font-medium text-gray-600 mb-2">Quotes Needed</p>
                    <p className="text-5xl font-bold text-blue-600 mb-2">{stats.quotesNeeded || 0}</p>
                    <p className="text-sm text-gray-500">Waiting for quotes</p>
                  </div>
                  <div className="text-6xl">💰</div>
                </div>
              </Link>

              <Link
                to="/admin-portal/queue"
                className="bg-white rounded-xl border-2 border-orange-200 p-8 hover:border-orange-400 transition-all shadow-lg hover:shadow-xl"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-base font-medium text-gray-600 mb-2">Build Queue</p>
                    <p className="text-5xl font-bold text-orange-600 mb-2">{stats.inQueue}</p>
                    <p className="text-sm text-gray-500">Apps in queue</p>
                  </div>
                  <div className="text-6xl">📋</div>
                </div>
              </Link>

              <Link
                to="/admin-portal/ready-to-live"
                className="bg-white rounded-xl border-2 border-green-200 p-8 hover:border-green-400 transition-all shadow-lg hover:shadow-xl"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-base font-medium text-gray-600 mb-2">Ready to go Live</p>
                    <p className="text-5xl font-bold text-green-600 mb-2">{stats.readyToLive}</p>
                    <p className="text-sm text-gray-500">Approved & ready</p>
                  </div>
                  <div className="text-6xl">🚀</div>
                </div>
              </Link>

              <Link
                to="/admin-portal/apps"
                className="bg-white rounded-xl border-2 border-purple-200 p-8 hover:border-purple-400 transition-all shadow-lg hover:shadow-xl"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-base font-medium text-gray-600 mb-2">All Apps</p>
                    <p className="text-5xl font-bold text-purple-600 mb-2">{stats.totalApps || 0}</p>
                    <p className="text-sm text-gray-500">Total apps</p>
                  </div>
                  <div className="text-6xl">📱</div>
                </div>
              </Link>

              <Link
                to="/admin-portal/apps?filter=live"
                className="bg-white rounded-xl border-2 border-zapt-purple/30 p-8 hover:border-zapt-purple transition-all shadow-lg hover:shadow-xl"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-base font-medium text-gray-600 mb-2">Live Apps</p>
                    <p className="text-5xl font-bold text-zapt-purple mb-2">{stats.live}</p>
                    <p className="text-sm text-gray-500">Total in production</p>
                  </div>
                  <div className="text-6xl">✅</div>
                </div>
              </Link>

              <Link
                to="/admin-portal/payment-issues"
                className={`bg-white rounded-xl border-2 p-8 transition-all shadow-lg hover:shadow-xl ${
                  stats.paymentIssues > 0
                    ? 'border-red-300 hover:border-red-400'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-base font-medium text-gray-600 mb-2">Payment Issues</p>
                    <p className={`text-5xl font-bold mb-2 ${
                      stats.paymentIssues > 0 ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {stats.paymentIssues || 0}
                    </p>
                    <p className="text-sm text-gray-500">
                      {stats.paymentIssues > 0 ? 'Apps with payment problems' : 'All payments OK'}
                    </p>
                  </div>
                  <div className="text-6xl">{stats.paymentIssues > 0 ? '⚠️' : '✅'}</div>
                </div>
              </Link>

              <Link
                to="/admin-portal/apps-no-payment-method"
                className={`bg-white rounded-xl border-2 p-8 transition-all shadow-lg hover:shadow-xl ${
                  stats.appsNoPaymentMethod > 0
                    ? 'border-yellow-300 hover:border-yellow-400'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-base font-medium text-gray-600 mb-2">Apps Needing Payment Setup</p>
                    <p className={`text-5xl font-bold mb-2 ${
                      stats.appsNoPaymentMethod > 0 ? 'text-yellow-600' : 'text-gray-600'
                    }`}>
                      {stats.appsNoPaymentMethod || 0}
                    </p>
                    <p className="text-sm text-gray-500">
                      {stats.appsNoPaymentMethod > 0 ? 'Need work but no payment method' : 'All apps have payment methods'}
                    </p>
                  </div>
                  <div className="text-6xl">{stats.appsNoPaymentMethod > 0 ? '💳' : '✅'}</div>
                </div>
              </Link>
            </div>
          )}

          {loadingStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i} className="bg-white rounded-xl border-2 border-gray-200 p-8 animate-pulse">
                  <div className="h-5 bg-gray-200 rounded w-32 mb-4"></div>
                  <div className="h-12 bg-gray-200 rounded w-20 mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded w-40"></div>
                </div>
              ))}
            </div>
          )}

          {/* Email Templates Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Tools</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Link
                to="/admin-portal/email-templates"
                className="bg-white rounded-xl border-2 border-gray-200 p-8 hover:border-gray-300 transition-all shadow-lg hover:shadow-xl"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-base font-medium text-gray-600 mb-2">Email Templates</p>
                    <p className="text-5xl font-bold text-gray-600 mb-2">📧</p>
                    <p className="text-sm text-gray-500">Preview all email templates</p>
                  </div>
                  <div className="text-6xl">📧</div>
                </div>
              </Link>

              <Link
                to="/admin-portal/errors"
                className={`bg-white rounded-xl border-2 p-8 transition-all shadow-lg hover:shadow-xl ${
                  stats.newErrors > 0
                    ? 'border-red-300 hover:border-red-400'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-base font-medium text-gray-600 mb-2">Error Logs</p>
                    <p className={`text-5xl font-bold mb-2 ${
                      stats.newErrors > 0 ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {stats.newErrors || 0}
                    </p>
                    <p className="text-sm text-gray-500">
                      {stats.newErrors > 0 ? 'New errors to review' : 'No new errors'}
                    </p>
                  </div>
                  <div className="text-6xl">{stats.newErrors > 0 ? '🚨' : '✅'}</div>
                </div>
              </Link>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-6">
            <button
              onClick={() => setShowCreateAppModal(true)}
              className="px-6 py-3 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 transition-colors font-medium"
            >
              + Create App for User
            </button>
          </div>
      </AdminPortalLayout>
      
      <AdminCreateAppModal
        isOpen={showCreateAppModal}
        onClose={() => setShowCreateAppModal(false)}
        onSuccess={() => {
          setShowCreateAppModal(false);
        }}
        authFetch={authFetch}
        showToast={showToast}
      />
    </AdminAccess>
  );
}

